import { STORAGE_KEYS } from '@constants/storage';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem
} from '@utils/storage';

export const getUserId = (): string => {
  const userId = getLocalStorageItem(STORAGE_KEYS.USER_ID);
  if (userId == null || userId === '') return 'anonymous';
  return String(userId);
};

export const getBranchSegment = (): string => {
  const orgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID) ?? '';
  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID) ?? '';
  if (!orgId || !branchId) return 'unknown';
  return `${orgId}:${branchId}`;
};

/** Read a localStorage key that is expected to be a plain JSON object. */
export const readJsonObject = <T extends Record<string, unknown>>(
  key: string
): T => {
  try {
    const raw = getLocalStorageItem(key);
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as T;
    }
  } catch {
    /* corrupted value — fall through to empty */
  }
  return {} as T;
};

const DYNAMIC_STORAGE_KEYS = [
  STORAGE_KEYS.DISMISSED_DYNAMIC_COMPONENTS,
  STORAGE_KEYS.DYNAMIC_COMPONENT_MIXPANEL_VIEWS
];

/**
 * Call on login after setting the user ID. If the new user ID
 * differs from the one that owns the stored data, wipe the
 * previous user's data so the new user starts fresh.
 */
export const clearStaleDynamicComponentStorage = (
  currentUserId: string
): void => {
  DYNAMIC_STORAGE_KEYS.forEach((key) => {
    const root = readJsonObject<Record<string, unknown>>(key);
    const existingKeys = Object.keys(root);

    if (existingKeys.length === 0) return;

    const hasCurrentUser = existingKeys.includes(currentUserId);
    const hasOtherUsers = existingKeys.some((k) => k !== currentUserId);

    if (hasOtherUsers) {
      if (hasCurrentUser) {
        setLocalStorageItem(key, { [currentUserId]: root[currentUserId] });
      } else {
        removeLocalStorageItem(key);
      }
    }
  });
};

/**
 * Generic scoped-storage helper.
 *
 * Shape in localStorage: `{ [userId]: V }`. On login,
 * `clearStaleUserStorage` removes previous user data if the
 * user ID changed.
 */
export class ScopedStorage<V> {
  constructor(
    private readonly storageKey: string,
    private readonly emptyValue: V
  ) {}

  private readRoot(): Record<string, V> {
    return readJsonObject<Record<string, V>>(this.storageKey);
  }

  read(scopeKey: string): V {
    const val = this.readRoot()[scopeKey];
    if (val != null && typeof val === typeof this.emptyValue) return val;
    return this.emptyValue;
  }

  write(scopeKey: string, value: V): void {
    const root = this.readRoot();
    setLocalStorageItem(this.storageKey, { ...root, [scopeKey]: value });
  }
}
